import * as React from "react";

import FormCardContainer from "@impero-com/common/components/formCardContainer";
import RightSideIcon from "@impero-com/common/components/rightSideIcon";
import passwordSvg from "@impero-com/common/img/password-login.svg";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import {
    isLoginTimeout,
    isUnauthenticated,
} from "@impero-com/common/utils/auth";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { loginRedirectToRoot } from "@impero-com/common/utils/url";
import { Button, FormSubtitle, FormTitle } from "@impero-com/ui";

import { Authenticated } from "api/generated/model";
import { postValidatePin } from "api/generated/two-factor/two-factor";

import { PinCodeForm } from "../components/twoFactor";

/*
 * Two factor form
 */
export default class TwoFactorForm extends React.Component<
    TwoFactorFormProps,
    TwoFactorFormState
> {
    constructor(props: TwoFactorFormProps) {
        super(props);
        this.state = {
            status: { type: "DEFAULT" },
            wrongPin: false,
            requiresHumanVerification: false,
        };
    }

    render() {
        const { wrongPin, requiresHumanVerification } = this.state;
        const { orgCookie } = this.props;
        return (
            <FormCardContainer
                rightSide={
                    <RightSideIcon
                        icon={passwordSvg}
                        customImages={true}
                        orgCookie={orgCookie}
                    />
                }
            >
                <FormTitle title={t("MyProfile_TwoFactor_Title")} />
                <FormSubtitle
                    id={"subtitle-sms-sent"}
                    subtitle={t("Setup2FA_Subtitle")}
                />

                <PinCodeForm
                    challenge={this.props.challenge}
                    onSubmitPin={this.handleSubmitPin}
                    onSwitchToRecovery={this.handleSwitchToRecovery}
                    submitButtonText={t("Login_LoginButton")}
                    wrongPin={wrongPin}
                    requiresHumanVerification={requiresHumanVerification}
                >
                    <Button
                        id="recovery-link"
                        variant="link"
                        onClick={this.handleSwitchToRecovery}
                    >
                        {t("Login2FA_ForgotYourDevice")}
                    </Button>
                </PinCodeForm>
            </FormCardContainer>
        );
    }

    handleSwitchToRecovery = () => {
        const { challenge, onSwitchToRecovery } = this.props;
        onSwitchToRecovery(challenge);
    };

    handleSubmitPin = (pin: string, captcha: string | null) => {
        this.setState({ wrongPin: false });
        const { ajaxManager } = this.props;
        ajaxManager
            .ajax<Authenticated, TwoFactorFormStatus>(
                () =>
                    postValidatePin({
                        pin,
                        challenge: this.props.challenge,
                        human_verification_response: captcha,
                    }),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    getSuccessStatus: () => ({ type: "DEFAULT" }),
                    getErrorStatus: () => ({ type: "DEFAULT" }),
                    inFlightStatus: { type: "VALIDATING_PIN" },
                    onErrorCode: {
                        401: (err) => {
                            const data = err.response?.data;
                            this.setState((state) => ({
                                wrongPin: true,
                                requiresHumanVerification:
                                    state.requiresHumanVerification ||
                                    (isUnauthenticated(data) &&
                                        data.status === "notAuthenticated" &&
                                        data.requiresHumanVerification),
                            }));
                        },
                        403: () => this.props.onNeedCredentials(),
                        408: (err) => {
                            const data = err.response?.data;
                            if (!isLoginTimeout(data)) {
                                return;
                            }
                            switch (data.cause) {
                                case "pinCode":
                                    this.setState({ wrongPin: true });
                                    break;
                                case "credentials":
                                    this.props.onNeedCredentials();
                                    break;
                                default:
                                    assertNever(data.cause);
                            }
                        },
                    },
                }
            )
            .then(loginRedirectToRoot);
    };
}
interface TwoFactorFormProps {
    onSwitchToRecovery: (challenge: string) => void;
    onNeedCredentials: () => void;
    challenge: string;
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
}

type TwoFactorFormStatus = { type: "DEFAULT" } | { type: "VALIDATING_PIN" };
interface TwoFactorFormState {
    status: TwoFactorFormStatus;
    wrongPin: boolean;
    requiresHumanVerification: boolean;
}
